
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, User, ArrowLeft, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import { toast } from "@/hooks/use-toast";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface BlockedUser {
  blocked_id: string;
  display_name: string;
  photo_urls: string[];
  blocked_at: string;
}

const BlockList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBlockedUsers();
    }
  }, [user]);

  const fetchBlockedUsers = async () => {
    if (!user) return;

    try {
      // First get the blocked user IDs
      const { data: blocksData, error: blocksError } = await supabase
        .from('user_blocks')
        .select('blocked_id, created_at')
        .eq('blocker_id', user.id);

      if (blocksError) throw blocksError;

      if (!blocksData || blocksData.length === 0) {
        setBlockedUsers([]);
        return;
      }

      // Then get the profiles for those blocked users
      const blockedIds = blocksData.map(block => block.blocked_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls')
        .in('user_id', blockedIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const blocked = blocksData.map(block => {
        const profile = profilesData?.find(p => p.user_id === block.blocked_id);
        return {
          blocked_id: block.blocked_id,
          display_name: profile?.display_name || 'Unknown User',
          photo_urls: profile?.photo_urls || [],
          blocked_at: block.created_at
        };
      });

      setBlockedUsers(blocked);
    } catch (error) {
      console.error('Error fetching blocked users:', error);
      toast({
        title: "Error",
        description: "Failed to load blocked users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (blockedUserId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_blocks')
        .delete()
        .eq('blocker_id', user.id)
        .eq('blocked_id', blockedUserId);

      if (error) throw error;

      setBlockedUsers(prev => prev.filter(u => u.blocked_id !== blockedUserId));
      
      toast({
        title: "Success",
        description: "User has been unblocked",
      });
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast({
        title: "Error",
        description: "Failed to unblock user",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-carbon flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
        <AdPanel />
        <RightSideMenu />
        <div className="flex-1 flex items-center justify-center">
          <AlphaLoader size="small" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-carbon text-ice-white flex flex-col ${isRTL ? 'rtl' : 'ltr'}`}>
      <AdPanel />
      <RightSideMenu />
      
      <div className="flex-1 p-4 pb-20">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="text-ice-white/70 hover:text-ice-white hover:bg-ice-white/10"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-2xl font-bold text-ice-white">Blocked Users</h1>
        </div>

        {blockedUsers.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-ice-white/30 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-ice-white/60 mb-2">No blocked users</h2>
            <p className="text-ice-white/40">Users you block will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {blockedUsers.map((blockedUser) => (
              <Card key={blockedUser.blocked_id} className="bg-carbon border-ice-white/10">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Profile Photo */}
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        {blockedUser.photo_urls && blockedUser.photo_urls.length > 0 ? (
                          <img
                            src={blockedUser.photo_urls[0]}
                            alt={blockedUser.display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-ice-white/10 flex items-center justify-center">
                            <User className="w-6 h-6 text-ice-white/50" />
                          </div>
                        )}
                      </div>

                      {/* User Info */}
                      <div>
                        <h3 className="font-semibold text-ice-white">{blockedUser.display_name}</h3>
                        <p className="text-sm text-ice-white/50">
                          Blocked {new Date(blockedUser.blocked_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {/* Unblock Button */}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleUnblock(blockedUser.blocked_id)}
                      className="bg-neon-red hover:bg-neon-red/80 text-ice-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Unblock
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlockList;
